import { useState, type ReactNode } from 'react';
import { useAppFeatures } from '../app-features/api.js';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  MEDIA_VOCAB_QUERY_KEY,
  bulkMedia,
  listVocab,
  mediaListQueryKey,
  type BulkMediaOp,
  type BulkMediaResult,
  type Vocab,
} from './api.js';
import type {
  AiInvolvement,
  MediaComplianceFieldsPatch,
  PeopleConsent,
  RightsBasis,
} from './types.js';
import {
  AI_INVOLVEMENT_LABEL,
  PEOPLE_CONSENT_LABEL,
  RIGHTS_BASIS_LABEL,
  RIGHTS_CONFIRMED_REF_HINT,
  buildNewRightsConfirmation,
  showsAiChangeNote,
  showsRightsConfirmedRef,
  showsStockFields,
  toPeopleRecognizable,
  type PeopleRecognizableChoice,
} from './media-compliance-fields.js';
import { PeopleRecognizableRadioGroup } from './media-people-radio.js';
import { Dialog } from '../../components/ui/dialog.js';
import { Button } from '../../components/ui/button.js';
import { Field, FormActions } from '../../components/ui/form.js';
import { Input } from '../../components/ui/input.js';
import { Select } from '../../components/ui/select.js';
import { useConfirm } from '../../components/ui/confirm-dialog.js';
import { cn } from '../../lib/utils.js';

type Panel = 'add' | 'remove' | 'compliance' | null;

/** Which Hungarian past-participle goes with each op in the result sentence. */
function verbFor(op: BulkMediaOp): string {
  switch (op) {
    case 'delete':
      return 'törölve';
    case 'add_tags':
      return 'megcímkézve';
    case 'remove_tags':
    case 'set_compliance':
      return 'frissítve';
  }
}

/**
 * Turns a `bulkMedia` result into one honest Hungarian sentence. The server
 * tells apart three outcomes per request — how many rows it actually changed
 * (`affected`), how many it refused because a post still uses them
 * (`skippedInUse`), and how many ids it could not find at all (`notFound`).
 * Collapsing that into a bare "kész" would hide a real partial failure from
 * the person who just asked for it — this is why all three are named here.
 */
function describeBulkResult(op: BulkMediaOp, result: BulkMediaResult): string {
  const parts = [`${result.affected} kép ${verbFor(op)}`];
  if (result.skippedInUse && result.skippedInUse.length > 0) {
    parts.push(`${result.skippedInUse.length} kihagyva (bejegyzésben szerepel)`);
  }
  if (result.notFound && result.notFound.length > 0) {
    parts.push(`${result.notFound.length} nem található`);
  }
  return `${parts.join(', ')}.`;
}

function describeBulkError(err: unknown): string {
  return err instanceof Error && err.message ? err.message : 'A művelet nem sikerült.';
}

/**
 * One "I'm setting this too" switch plus the fields it governs. Collapsed
 * (switched off) the fields are not rendered at all, so there is nothing on
 * screen suggesting a value is about to be written — which is exactly the
 * impression the old always-on form gave, right before it wrote one.
 */
function GroupSwitch({
  id,
  label,
  checked,
  onChange,
  children,
}: {
  id: string;
  label: string;
  checked: boolean;
  onChange: (next: boolean) => void;
  children: ReactNode;
}) {
  return (
    <div className="space-y-3 rounded-md border border-[var(--app-divider)] p-3">
      <div className="flex items-center gap-2">
        <input
          id={id}
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          className="h-4 w-4 cursor-pointer rounded border-[var(--app-divider-2)]"
        />
        <label htmlFor={id} className="text-body font-medium text-[var(--ink-700)]">
          {label}
        </label>
      </div>
      {checked && children}
    </div>
  );
}

/**
 * Floating bar surfaced once one or more images are selected in the gallery
 * (wiring into the gallery page itself is a later task — this component only
 * needs `selectedIds`). Four actions marketing needs to clear the September
 * image-policy backlog: tag a whole shoot at once, untag it (including words
 * the tag-dictionary migration archived — see `removableTags` below), set the
 * rights answers on every selected image in one request, or delete the ones
 * that turned out unusable.
 */
export function MediaBulkBar({
  hotelId,
  selectedIds,
  onDone,
}: {
  hotelId: string;
  selectedIds: string[];
  /**
   * Called once a bulk operation succeeds, with the op that ran. The bar
   * itself never clears `selectedIds` (it doesn't own that state) — a
   * caller that wants to keep working the same set after tagging or a
   * rights update (and only drop the selection after a delete) branches on
   * this argument.
   */
  onDone: (op: BulkMediaOp) => void;
}) {
  const count = selectedIds.length;
  const confirm = useConfirm();
  const qc = useQueryClient();

  const [panel, setPanel] = useState<Panel>(null);
  const [resultMessage, setResultMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const vocabQuery = useQuery<Vocab>({
    queryKey: MEDIA_VOCAB_QUERY_KEY,
    queryFn: () => listVocab(),
    enabled: count > 0,
  });
  const vocab = vocabQuery.data ?? { categories: [], tags: [] };

  const [addSelection, setAddSelection] = useState<string[]>([]);
  const [removeSelection, setRemoveSelection] = useState<string[]>([]);

  // Same two mandatory questions, same conditional fields, same defaults as
  // `MediaUploadPanel` — this form asks the identical question of many files
  // at once, so it must behave identically.
  const [rightsBasis, setRightsBasis] = useState<RightsBasis>('unknown');
  const [aiInvolvement, setAiInvolvement] = useState<AiInvolvement>('none');
  const [rightsConfirmedRef, setRightsConfirmedRef] = useState('');
  const [stockPlatform, setStockPlatform] = useState('');
  const [stockTerritory, setStockTerritory] = useState('');
  const [licenseExpiresAt, setLicenseExpiresAt] = useState('');
  const [aiChangeNote, setAiChangeNote] = useState('');
  const [peopleChoice, setPeopleChoice] = useState<PeopleRecognizableChoice>('unknown');
  const [peopleConsent, setPeopleConsent] = useState<PeopleConsent>('none');

  // "I'm setting this too" — one switch per field group (rights, AI,
  // people). Only a switched-on group reaches the payload. Without this the
  // form sent its own DEFAULTS for every group on every request: a September
  // triage run that set nothing but the rights basis on 200 images also
  // declared, about all 200, that no AI touched them and that nobody
  // recognisable is on them — a legal statement no one made (finding C2).
  //
  // Why a switch and not change-tracking: with change-tracking the default
  // value of a group (the "Nem ismert" rights basis, say) could never be set
  // on purpose, because selecting it changes nothing. The switch is explicit
  // and testable.
  const [setsRights, setSetsRights] = useState(false);
  const [setsAi, setSetsAi] = useState(false);
  const [setsPeople, setSetsPeople] = useState(false);
  const hasGroup = setsRights || setsAi || setsPeople;

  const showRightsRef = showsRightsConfirmedRef(rightsBasis);
  const showStockFields = showsStockFields(rightsBasis);
  const showAiChangeNote = showsAiChangeNote(aiInvolvement);
  const showPeopleConsent = peopleChoice === 'yes';
  const features = useAppFeatures();
  const complianceOff = features.data?.mediaComplianceMode === 'off';

  const mutation = useMutation({
    mutationFn: (args: {
      op: BulkMediaOp;
      tags?: string[];
      compliance?: MediaComplianceFieldsPatch;
    }) => bulkMedia(hotelId, { ids: selectedIds, ...args }),
    onSuccess: (result, vars) => {
      setErrorMessage(null);
      setResultMessage(describeBulkResult(vars.op, result));
      setPanel(null);
      setAddSelection([]);
      setRemoveSelection([]);
      // So the grid reflects the change (new tags, updated rights, or the
      // image gone) without a manual reload — invalidate every loaded
      // (filter, cursor) page for this hotel, not just the currently
      // visible one.
      void qc.invalidateQueries({ queryKey: mediaListQueryKey(hotelId) });
      onDone(vars.op);
    },
    onError: (err) => setErrorMessage(describeBulkError(err)),
  });

  // Stay mounted while there is EITHER a live selection OR a result to show
  // — a caller that clears `selectedIds` right after `onDone` fires (the
  // delete case) must not also erase the partial-delete accounting
  // (affected / skippedInUse / notFound) the person just asked for.
  if (count === 0 && resultMessage === null && errorMessage === null) return null;

  /**
   * ONLY the switched-on groups' keys. An absent key means "leave this
   * column alone" all the way down — `MediaComplianceFieldsPatchSchema`
   * (`packages/schemas/src/hotel-media.ts`) is all-optional exactly so this
   * is expressible.
   *
   * A group carries its own conditional fields: the stock trio only when the
   * basis is `stock`, the change note only when AI touched the image. On a
   * non-stock basis the old stock columns are left as they are rather than
   * nulled — the compliance engine ignores them unless the basis is `stock`,
   * so stale values there are inert, and writing nulls we were not asked for
   * is the very habit this fix removes.
   */
  function buildCompliance(): MediaComplianceFieldsPatch {
    const patch: MediaComplianceFieldsPatch = {};
    if (setsRights) {
      patch.rightsBasis = rightsBasis;
      Object.assign(patch, buildNewRightsConfirmation(rightsBasis, rightsConfirmedRef));
      if (showStockFields) {
        patch.stockPlatform = stockPlatform.trim() || null;
        patch.stockTerritory = stockTerritory.trim() || null;
        patch.licenseExpiresAt = licenseExpiresAt.trim() || null;
      }
    }
    if (setsAi) {
      patch.aiInvolvement = aiInvolvement;
      patch.aiChangeNote = showAiChangeNote ? aiChangeNote.trim() || null : null;
    }
    if (setsPeople) {
      patch.peopleRecognizable = toPeopleRecognizable(peopleChoice);
      // Cleared unless there IS somebody recognisable — a consent left over
      // from an earlier answer must not outlive the answer it belonged to.
      patch.peopleConsent = showPeopleConsent ? peopleConsent : null;
    }
    return patch;
  }

  async function handleDelete() {
    const ok = await confirm({
      title: `${count} kép törlése`,
      message:
        'A törlés nem visszavonható. Amit egy poszt még használ, azt a szerver megtartja, és külön jelzi.',
      confirmLabel: 'Igen, törlöm',
      tone: 'destructive',
    });
    if (ok) mutation.mutate({ op: 'delete' });
  }

  function toggle(list: string[], setList: (next: string[]) => void, tag: string) {
    setList(list.includes(tag) ? list.filter((t) => t !== tag) : [...list, tag]);
  }

  // Only current words can be added — a retired one has no business landing
  // on a different image from here.
  const addableTags = vocab.tags.filter((t) => t.archivedAt === null);
  // Removal must offer archived words too: the tag-dictionary migration swept
  // every previously free-typed word into the archive, and those are exactly
  // the words a colleague is clearing off old images this September. A list
  // limited to current tags would be useless for that job.
  const removableTags = vocab.tags;

  // Audit I11: a failed dictionary fetch used to render as "Nincs elérhető
  // címke a szótárban." — a real error shown as an empty dictionary. The
  // empty-state copy is now only shown once the fetch has actually succeeded;
  // loading and error each get their own line.
  const vocabStatus = vocabQuery.isError ? (
    <p role="alert" className="flex flex-wrap items-center gap-2 text-body text-[var(--danger)]">
      A címkeszótár nem tölthető be.
      <Button size="sm" variant="outline" onClick={() => void vocabQuery.refetch()}>
        Újra
      </Button>
    </p>
  ) : vocabQuery.isPending ? (
    <p className="text-body text-[var(--fg-faint)]">Betöltés…</p>
  ) : null;

  return (
    // Fix round 1, task 31 (a bug this round's e2e run surfaced): the three
    // <Dialog>s below used to render as CHILDREN of the floating pill, which
    // carries `-translate-x-1/2`. A `transform` on an ancestor turns it into
    // the containing block for any `position: fixed` descendant — so each
    // Dialog's own `fixed inset-0` centered itself against the pill's small
    // bounding box (bottom of the screen) instead of the real viewport,
    // pushing the dialog's lower half — including its primary action button
    // — off-screen. A real click on "Jogállás beállítása" (or either tag
    // panel) could genuinely not be completed. Rendering the dialogs as
    // SIBLINGS of the transformed pill, both under a plain (untransformed)
    // fragment, gives them the real viewport as their containing block again.
    <>
      <div
        className="fixed bottom-6 left-1/2 z-40 flex -translate-x-1/2 flex-wrap items-center gap-2 rounded-xl border border-[var(--app-divider)] bg-[var(--app-surface)] px-3 py-2 shadow-2xl"
        role="region"
        aria-label="Tömeges média-műveletek"
      >
        {count > 0 && (
          <>
            <span className="rounded-full bg-[var(--brand-500)] px-2.5 py-0.5 text-meta font-semibold text-white">
              {count} kijelölve
            </span>

            <Button
              variant="outline"
              size="sm"
              onClick={() => setPanel('add')}
              disabled={mutation.isPending}
            >
              Címke hozzáadása
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPanel('remove')}
              disabled={mutation.isPending}
            >
              Címke eltávolítása
            </Button>
            {!complianceOff && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPanel('compliance')}
                disabled={mutation.isPending}
              >
                Jogállás beállítása
              </Button>
            )}
            <Button
              variant="destructiveSoft"
              size="sm"
              onClick={() => void handleDelete()}
              disabled={mutation.isPending}
            >
              Törlés
            </Button>
          </>
        )}

        {errorMessage && <span className="text-meta text-[var(--danger)]">{errorMessage}</span>}
        {!errorMessage && resultMessage && (
          <span className="text-meta text-[var(--fg-2)]">{resultMessage}</span>
        )}
      </div>

      <Dialog
        open={panel === 'add'}
        onClose={() => setPanel(null)}
        title="Címke hozzáadása"
        size="md"
      >
        <div className="space-y-3">
          <p className="text-body text-[var(--fg-2)]">
            A kiválasztott címkék felkerülnek mind a(z) {count} kijelölt képre.
          </p>
          <div className="flex flex-wrap gap-1.5">
            {addableTags.map((t) => {
              const chosen = addSelection.includes(t.tag);
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => toggle(addSelection, setAddSelection, t.tag)}
                  aria-pressed={chosen}
                  className={cn(
                    'rounded-pill border px-2 py-0.5 text-meta',
                    chosen
                      ? 'border-[var(--brand-500)] bg-[var(--brand-100)] text-[var(--brand-800)]'
                      : 'border-[var(--app-divider-2)] text-[var(--fg-2)] hover:bg-[var(--app-hover)]',
                  )}
                >
                  {t.tag}
                </button>
              );
            })}
            {vocabStatus}
            {vocabQuery.isSuccess && addableTags.length === 0 && (
              <p className="text-body text-[var(--fg-faint)]">Nincs elérhető címke a szótárban.</p>
            )}
          </div>
          <FormActions>
            <Button variant="ghost" onClick={() => setPanel(null)}>
              Mégse
            </Button>
            <Button
              disabled={addSelection.length === 0 || mutation.isPending}
              onClick={() => mutation.mutate({ op: 'add_tags', tags: addSelection })}
            >
              Alkalmaz
            </Button>
          </FormActions>
        </div>
      </Dialog>

      <Dialog
        open={panel === 'remove'}
        onClose={() => setPanel(null)}
        title="Címke eltávolítása"
        size="md"
      >
        <div className="space-y-3">
          <p className="text-body text-[var(--fg-2)]">
            A kiválasztott címkék lekerülnek mind a(z) {count} kijelölt képről — archivált szó is
            választható, a régi képek gyakran azokat viselik.
          </p>
          <div className="flex flex-wrap gap-1.5">
            {removableTags.map((t) => {
              const chosen = removeSelection.includes(t.tag);
              const archived = t.archivedAt !== null;
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => toggle(removeSelection, setRemoveSelection, t.tag)}
                  aria-pressed={chosen}
                  className={cn(
                    'rounded-pill border px-2 py-0.5 text-meta',
                    archived && 'italic',
                    chosen
                      ? 'border-[var(--brand-500)] bg-[var(--brand-100)] text-[var(--brand-800)]'
                      : 'border-[var(--app-divider-2)] text-[var(--fg-2)] hover:bg-[var(--app-hover)]',
                  )}
                >
                  {t.tag}
                  {archived && ' (archív)'}
                </button>
              );
            })}
            {vocabStatus}
            {vocabQuery.isSuccess && removableTags.length === 0 && (
              <p className="text-body text-[var(--fg-faint)]">Nincs címke a szótárban.</p>
            )}
          </div>
          <FormActions>
            <Button variant="ghost" onClick={() => setPanel(null)}>
              Mégse
            </Button>
            <Button
              disabled={removeSelection.length === 0 || mutation.isPending}
              onClick={() => mutation.mutate({ op: 'remove_tags', tags: removeSelection })}
            >
              Alkalmaz
            </Button>
          </FormActions>
        </div>
      </Dialog>

      <Dialog
        open={panel === 'compliance'}
        onClose={() => setPanel(null)}
        title="Jogállás beállítása"
        size="2xl"
      >
        <div className="space-y-4">
          <p className="text-body text-[var(--fg-2)]">
            Csak azt írjuk felül, amit itt bekapcsolsz — mind a(z) {count} kijelölt képen. Amit
            kikapcsolva hagysz, az minden képen érintetlen marad.
          </p>

          <GroupSwitch
            id="mbb-sets-rights"
            label="Jogalapot állítok"
            checked={setsRights}
            onChange={setSetsRights}
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Jogalap" htmlFor="mbb-rights-basis">
                <Select
                  id="mbb-rights-basis"
                  value={rightsBasis}
                  onChange={(e) => setRightsBasis(e.target.value as RightsBasis)}
                >
                  {(Object.keys(RIGHTS_BASIS_LABEL) as RightsBasis[]).map((v) => (
                    <option key={v} value={v}>
                      {RIGHTS_BASIS_LABEL[v]}
                    </option>
                  ))}
                </Select>
              </Field>

              {showRightsRef && (
                <Field
                  label="Megerősítés hivatkozása"
                  htmlFor="mbb-rights-ref"
                  hint={RIGHTS_CONFIRMED_REF_HINT}
                >
                  <Input
                    id="mbb-rights-ref"
                    value={rightsConfirmedRef}
                    onChange={(e) => setRightsConfirmedRef(e.target.value)}
                  />
                </Field>
              )}

              {showStockFields && (
                <>
                  <Field label="Stock platform" htmlFor="mbb-stock-platform">
                    <Input
                      id="mbb-stock-platform"
                      value={stockPlatform}
                      onChange={(e) => setStockPlatform(e.target.value)}
                      placeholder="pl. Shutterstock"
                    />
                  </Field>
                  <Field label="Terület" htmlFor="mbb-stock-territory">
                    <Input
                      id="mbb-stock-territory"
                      value={stockTerritory}
                      onChange={(e) => setStockTerritory(e.target.value)}
                      placeholder="pl. világ / EU / HU"
                    />
                  </Field>
                  <Field label="Licenc lejárata" htmlFor="mbb-license-expires">
                    <Input
                      id="mbb-license-expires"
                      type="date"
                      value={licenseExpiresAt}
                      onChange={(e) => setLicenseExpiresAt(e.target.value)}
                    />
                  </Field>
                </>
              )}
            </div>
          </GroupSwitch>

          <GroupSwitch
            id="mbb-sets-ai"
            label="AI-érintettséget állítok"
            checked={setsAi}
            onChange={setSetsAi}
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="AI-érintettség" htmlFor="mbb-ai-involvement">
                <Select
                  id="mbb-ai-involvement"
                  value={aiInvolvement}
                  onChange={(e) => setAiInvolvement(e.target.value as AiInvolvement)}
                >
                  {(Object.keys(AI_INVOLVEMENT_LABEL) as AiInvolvement[]).map((v) => (
                    <option key={v} value={v}>
                      {AI_INVOLVEMENT_LABEL[v]}
                    </option>
                  ))}
                </Select>
              </Field>

              {showAiChangeNote && (
                <Field label="Mi módosult?" htmlFor="mbb-ai-change-note">
                  <Input
                    id="mbb-ai-change-note"
                    value={aiChangeNote}
                    onChange={(e) => setAiChangeNote(e.target.value)}
                    placeholder="pl. háttér cserélve, tárgy eltávolítva"
                  />
                </Field>
              )}
            </div>
          </GroupSwitch>

          <GroupSwitch
            id="mbb-sets-people"
            label="Személyeket állítok"
            checked={setsPeople}
            onChange={setSetsPeople}
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <PeopleRecognizableRadioGroup
                namePrefix="mbb"
                value={peopleChoice}
                onChange={setPeopleChoice}
              />

              {showPeopleConsent && (
                <Field label="Hozzájárulás" htmlFor="mbb-people-consent">
                  <Select
                    id="mbb-people-consent"
                    value={peopleConsent}
                    onChange={(e) => setPeopleConsent(e.target.value as PeopleConsent)}
                  >
                    {(Object.keys(PEOPLE_CONSENT_LABEL) as PeopleConsent[]).map((v) => (
                      <option key={v} value={v}>
                        {PEOPLE_CONSENT_LABEL[v]}
                      </option>
                    ))}
                  </Select>
                </Field>
              )}
            </div>
          </GroupSwitch>

          <FormActions>
            <Button variant="ghost" onClick={() => setPanel(null)}>
              Mégse
            </Button>
            <Button
              disabled={mutation.isPending || !hasGroup}
              onClick={() =>
                mutation.mutate({ op: 'set_compliance', compliance: buildCompliance() })
              }
            >
              Alkalmaz
            </Button>
          </FormActions>
        </div>
      </Dialog>
    </>
  );
}
